<template>
  <div class="container">
    <div v-if="loading" class="loading-container">
      <i class="fa fa-spinner fa-spin"></i> Loading route data...
    </div>

    <div v-else>
      <!-- Route Details Section -->
      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">1. Route Details</h4>
        </div>
        <div class="row mt-3">
          <div class="col-md-6 form-group">
            <label>Route Name <span class="text-danger">*</span></label>
            <input type="text" v-model="route.route_name" class="form-control" placeholder="Enter route name" />
          </div>
          <div class="col-md-6 form-group">
            <label>Distance (Km) <span class="text-danger">*</span></label>
            <input type="number" v-model="route.distance" class="form-control" placeholder="0.0" />
          </div>
          <div class="col-md-6 form-group">
            <label>Start Point <span class="text-danger">*</span></label>
            <input type="text" v-model="route.start_point" class="form-control" placeholder="Start location" />
          </div>
          <div class="col-md-6 form-group">
            <label>End Point <span class="text-danger">*</span></label>
            <input type="text" v-model="route.end_point" class="form-control" placeholder="End location" />
          </div>

          <div class="col-md-6 form-group">
            <label>Change Route Status:</label>
            <div class="toggle-switch-container">
              <label class="switch">
                <input type="checkbox" :checked="route.is_active == 1" @change="toggleRouteStatus">
                <span class="slider round"></span>
              </label>
              <span class="status-text" :class="route.is_active == 1 ? 'active' : 'inactive'">{{ route.is_active == 1 ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Existing Dealers Section -->
      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">2. Existing Dealers (Uncheck to Remove)</h4>
          
          <div class="header-actions">
            <div class="search-wrapper" v-if="isExistingOpen">
              <input 
                type="text" 
                v-model="searchExistingQuery" 
                placeholder="Search existing..." 
                class="header-search-input"
              />
              <i class="fa fa-search search-icon"></i>
            </div>
            <i 
              class="fa toggle-icon" 
              :class="isExistingOpen ? 'fa-angle-up' : 'fa-angle-down'" 
              @click="isExistingOpen = !isExistingOpen"
              title="Expand/Collapse"
            ></i>
          </div>
        </div>
        
        <div v-show="isExistingOpen" class="table-responsive mt-3">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Name</th>
                <th>Address</th>
                <th>City</th>
                <th class="text-center">Keep Assigned</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredExistingList" :key="dealer?.row_id || Math.random()">
                <td :title="dealer?.business_name">{{ truncateText(dealer?.business_name, 40) }}</td>
                <td :title="dealer?.address">{{ truncateText(dealer?.address, 55) }}</td>
                <td :title="dealer?.city">{{ truncateText(dealer?.city, 15) }}</td>
                <td class="text-center">
                  <input 
                    type="checkbox" 
                    class="big-checkbox"
                    :checked="isExistingSelected(dealer?.row_id)"
                    @change="toggleExisting(dealer?.row_id)"
                  />
                </td>
              </tr>
              <tr v-if="filteredExistingList.length === 0">
                <td colspan="4" class="text-center">
                  {{ searchExistingQuery ? 'No dealers found matching search.' : 'No existing dealers on this route.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Swap Dealer Section -->
      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">3. Swap Dealer (Re-assign)</h4>
          
          <div class="header-actions">
            <div class="search-wrapper" v-if="isSwapOpen">
              <input 
                type="text" 
                v-model="searchSwapQuery" 
                placeholder="Search swap dealers..." 
                class="header-search-input"
                @click.stop
              />
              <i class="fa fa-search search-icon"></i>
            </div>

            <i 
              class="fa toggle-icon" 
              :class="isSwapOpen ? 'fa-angle-up' : 'fa-angle-down'" 
              @click="isSwapOpen = !isSwapOpen"
              :title="isSwapOpen ? 'Collapse' : 'Expand'"
            ></i>
          </div>
        </div>
        
        <div v-show="isSwapOpen" class="table-responsive mt-3">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Name</th>
                <th>Address</th>
                <th>City</th>
                <th>Existing Route</th>
                <th class="text-center danger-zone">Action (Danger Zone)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredSwapList" :key="dealer.mapping_id">
                <td :title="dealer.business_name">{{ truncateText(dealer.business_name, 20) }}</td>
                <td :title="dealer.address">{{ truncateText(dealer.address, 25) }}</td>
                <td :title="dealer.city">{{ truncateText(dealer.city, 15) }}</td>
                <td :title="dealer.existing_route"><span class="badge badge-info">{{ truncateText(dealer.existing_route, 20) }}</span></td>
                <td class="text-center bg-light-danger">
                  <button 
                    type="button" 
                    class="btn btn-sm"
                    :class="isSwapped(dealer.mapping_id) ? 'btn-danger' : 'btn-success'"
                    @click="toggleSwap(dealer.mapping_id)"
                  >
                    {{ isSwapped(dealer.mapping_id) ? 'Unswap Route' : 'Swap Route' }}
                  </button>
                </td>
              </tr>
              <tr v-if="filteredSwapList.length === 0">
                <td colspan="5" class="text-center">
                  {{ searchSwapQuery ? 'No dealers found matching search.' : 'No dealers available to swap.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Map New Dealer Section -->
      <div class="section-card">
        <div class="section-header-row">
          <h4 class="section-title mb-0">4. Map New Dealer (Unassigned)</h4>
          
          <div class="header-actions">
            <div class="search-wrapper" v-if="isMapOpen">
              <input 
                type="text" 
                v-model="searchMapQuery" 
                placeholder="Search new dealers..." 
                class="header-search-input"
              />
              <i class="fa fa-search search-icon"></i>
            </div>
            <i 
              class="fa toggle-icon" 
              :class="isMapOpen ? 'fa-angle-up' : 'fa-angle-down'" 
              @click="isMapOpen = !isMapOpen"
              title="Expand/Collapse"
            ></i>
          </div>
        </div>

        <div v-show="isMapOpen" class="table-responsive mt-3">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Dealer Name</th>
                <th>Address</th>
                <th>City</th>
                <th class="text-center">Map (Select)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dealer in filteredMapList" :key="dealer?.dealer_profile_id || Math.random()">
                <td :title="dealer?.business_name">{{ truncateText(dealer?.business_name, 40) }}</td>
                <td :title="dealer?.address">{{ truncateText(dealer?.address, 55) }}</td>
                <td :title="dealer?.city">{{ truncateText(dealer?.city, 15) }}</td>
                <td class="text-center">
                  <input 
                    type="checkbox" 
                    class="big-checkbox"
                    :checked="isMapped(dealer?.dealer_profile_id)"
                    @change="toggleMap(dealer?.dealer_profile_id)"
                  />
                </td>
              </tr>
              <tr v-if="filteredMapList.length === 0">
                <td colspan="4" class="text-center">
                  {{ searchMapQuery ? 'No dealers found matching search.' : 'No unassigned dealers available.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button class="btn btn-danger" @click="cancel">Cancel</button>
        <button class="btn btn-success" @click="updateRoute" :disabled="updating">
          <span v-if="updating">
            <span class="spinner"></span>
          </span>
          <span v-else>
            Update Route
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { advanceViewRouteDetails, editRouteDetails } from "@/services/dataService"; 

export default {
  name: "EditRoute",
  props: {
    routeId: {
      type: String,
      required: true
    }
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      loading: true,
      updating: false,
      token: localStorage.getItem("token_set"),
      profileId: localStorage.getItem("profile_id"),
      
      // Section Toggles
      isExistingOpen: true,
      isSwapOpen: true,
      isMapOpen: true,
      
      // Search Queries
      searchExistingQuery: "",
      searchSwapQuery: "",
      searchMapQuery: "",

      route: {
        id: "",
        route_name: "",
        start_point: "",
        distance: "",
        end_point: "",
        is_active: "",
        distributor_id: "",
      },

      // Selected IDs Containers
      selectedExistingRowIds: [], 
      selectedSwapIds: [],
      selectedMapProfileIds: [],  

      // Data Lists
      existingDealersList: [],
      swapDealersList: [],
      mapDealersList: []
    };
  },
  computed: {
    // 1. Filter Existing
    filteredExistingList() {
      if (!this.existingDealersList) return [];
      if (!this.searchExistingQuery) return this.existingDealersList;
      
      const query = this.searchExistingQuery.toLowerCase();
      return this.existingDealersList.filter(dealer => {
        if (!dealer) return false;
        return (dealer.business_name || '').toLowerCase().includes(query);
      });
    },
    // 2. Filter Swap
    filteredSwapList() {
      if (!this.searchSwapQuery) return this.swapDealersList;
      const query = this.searchSwapQuery.toLowerCase();
      return this.swapDealersList.filter(dealer => 
        (dealer.business_name || '').toLowerCase().includes(query) ||
        (dealer.existing_route || '').toLowerCase().includes(query)
      );
    },
    // 3. Filter Map
    filteredMapList() {
      if (!this.mapDealersList) return [];
      if (!this.searchMapQuery) return this.mapDealersList;
      
      const query = this.searchMapQuery.toLowerCase();
      return this.mapDealersList.filter(dealer => {
        if (!dealer) return false;
        return (dealer.business_name || '').toLowerCase().includes(query);
      });
    }
  },
  mounted() {
    this.fetchRouteData();
  },
  methods: {
    // Helper to truncate long strings
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    async fetchRouteData() {
      this.loading = true;
      this.route = {};
      this.existingDealersList = [];
      this.swapDealersList = [];
      this.mapDealersList = [];

      try {
        // Calling the API with route ID
        const response = await advanceViewRouteDetails(this.token, this.routeId);

        if (response.data && response.data.status === 'success') {
          const data = response.data.data;

          // 1. Map Route Details
          this.route = data.routeDetails;

          // 2. Map Existing Dealers (Route Dealers)
          this.existingDealersList = data.routeDealers;

          // Initialize checked state: Select rows where route_mapping_status is "1"
          this.selectedExistingRowIds = this.existingDealersList
            .filter(d => d && String(d.route_mapping_status) === "1")
            .map(d => d.row_id);

          // 3. Map Swap Dealers (Other Route Dealers)
          this.swapDealersList = data.otherRouteDealers;

          // 4. Map New Dealers (Not Route Dealers)
          this.mapDealersList = data.notRouteDealers;

        } else {
          this.toast.error(response.data.message || "Failed to fetch data.");
        }
      } catch (error) {
        console.error("API Error:", error);
        this.toast.error("Failed to load route details.");
      } finally {
        this.loading = false;
      }
    },

    toggleRouteStatus() {
      this.route.is_active = this.route.is_active == 1 ? "0" : "1";
    },

    // Existing Logic
    isExistingSelected(id) {
      if (!id) return false;
      return this.selectedExistingRowIds.includes(id);
    },
    toggleExisting(id) {
      if (!id) return;
      if (this.isExistingSelected(id)) {
        this.selectedExistingRowIds = this.selectedExistingRowIds.filter(item => item !== id);
      } else {
        this.selectedExistingRowIds.push(id);
      }
    },

    // Swap Logic
    isSwapped(id) {
      return this.selectedSwapIds.includes(id);
    },
    toggleSwap(id) {
      if (this.isSwapped(id)) {
        this.selectedSwapIds = this.selectedSwapIds.filter(item => item !== id);
      } else {
        this.selectedSwapIds.push(id);
      }
    },

    // Map Logic
    isMapped(id) {
      if (!id) return false;
      return this.selectedMapProfileIds.includes(id);
    },
    toggleMap(id) {
      if (!id) return;
      if (this.isMapped(id)) {
        this.selectedMapProfileIds = this.selectedMapProfileIds.filter(item => item !== id);
      } else {
        this.selectedMapProfileIds.push(id);
      }
    },

    cancel() {
      this.$router.back(); 
    },

    validateForm() {
      if (!this.route || !this.route.route_name || this.route.route_name.trim() === '') {
        this.toast.error("Route name is required.");
        return false;
      }
      if (!this.route.distance) {
        this.toast.error("Distance is required."); 
        return false;
      }
      if (!this.route.start_point || this.route.start_point.trim() === '') {
        this.toast.error("Start point is required.");
        return false;
      }
      if (!this.route.end_point || this.route.end_point.trim() === '') {
        this.toast.error("End point is required.");
        return false;
      }
      return true;
    },

    async updateRoute() {
      if (!this.validateForm()) return;

      this.updating = true;

      // CALCULATE ENABLE/DISABLE LISTS (Compare Initial vs Current) ---
      let disableList = [];
      let enableList = [];

      this.existingDealersList.forEach(dealer => {
        if (!dealer || !dealer.row_id) return;

        const id = dealer.row_id;
        const initialStatus = String(dealer.route_mapping_status) === "1"; 
        const currentChecked = this.selectedExistingRowIds.includes(id);

        if (initialStatus && !currentChecked) {
          disableList.push(id); // Disable if it was active and now unchecked
        } else if (!initialStatus && currentChecked) {
          enableList.push(id); // Enable if it was inactive and now checked
        }
      });

      const payload = {
        "id": this.route.id,
        "route_name": this.route.route_name,
        "start_point": this.route.start_point,
        "distance": this.route.distance,
        "end_point": this.route.end_point,
        "updated_by": this.profileId, 
        "distributor_id": this.route.distributor_id,
        "is_active": this.route.is_active,
        "disable-route-mapping": disableList,
        "enable-route-mapping": enableList,
        "swap-dealer-route": this.selectedSwapIds,
        "map-dealer-route": this.selectedMapProfileIds
      };

      console.log("Update Payload:", JSON.stringify(payload, null, 2));

      try {
        // Call the API
        const response = await editRouteDetails(this.token, payload);
        if (response.data && response.data.status === 'success') {
          this.toast.success(response.data.message);
          this.$router.push('/routes');
        } else {
          this.toast.error(response.data.message);
        }
      } catch (error) {
        this.toast.error("An error occurred while updating route.");
      } finally {
        this.updating = false;
      }
    }
  }
};
</script>

<style scoped>
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  padding: 20px 15px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
  max-width: 100%;
}

.section-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
  background-color: #fff;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px; 
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.header-search-input {
  padding: 5px 30px 5px 10px; 
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  width: 200px;
  transition: border-color 0.3s;
}

.header-search-input:focus {
  border-color: #007bff;
  outline: none;
}

.search-icon {
  position: absolute;
  right: 10px;
  color: #999;
  font-size: 13px;
  pointer-events: none; 
}

.toggle-icon {
  cursor: pointer;
  color: #6c757d;
  font-size: 1.1rem;
  transition: color 0.3s ease;
  padding: 5px;
}

.toggle-icon:hover { 
  color: #007bff; transform: scale(1.1); 
}

.status-text {
  color: #6c757d !important;
  font-weight: 600;
}

.status-text.active {
  color: #28a745 !important;
}

.status-text.inactive {
  color: #dc3545 !important;
}

.mb-0 { 
  margin-bottom: 0 !important; 
}

.mt-3 { 
  margin-top: 1rem !important; 
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.col-md-6 {
  flex: 1 1 45%; 
  min-width: 300px;
}

.form-group { 
  margin-bottom: 10px; 
}

label {
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  color: #555;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box; 
}

select.form-control {
  appearance: auto;
}

.text-danger { 
  color: #dc3545; 
}

.text-success { 
  color: #28a745; 
}

.text-muted { 
  color: #6c757d; 
}

.table-responsive { 
  overflow-x: auto; 
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
}

.table th, .table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
}

.table th { 
  background-color: #f8f9fa; 
  font-weight: 600; 
  white-space: nowrap;
}

.text-center { 
  text-align: center !important; 
}

.big-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

.badge-info {
  background-color: #17a2b8;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.danger-zone {
  background-color: #f8d7da !important;
  color: #721c24;
  border-color: #f5c6cb;
}

.bg-light-danger { 
  background-color: #fff5f5; 
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-sm {
  padding: 5px 10px;
  font-size: 12px;
}

.btn-success { 
  background-color: #28a745; 
  color: white; 
}

.btn-success:hover { 
  background-color: #218838; 
}

.btn-danger { 
  background-color: #dc3545; 
  color: white; 
}

.btn-danger:hover { 
  background-color: #c82333; 
}

.btn-secondary { 
  background-color: #6c757d; 
  color: white; 
}

.btn-secondary:hover { 
  background-color: #5a6268; 
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #54ba4a;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container {
  text-align: center;
  padding: 50px;
  color: #007bff;
  font-size: 18px;
}

.toggle-switch-container {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
}

.switch {
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
}

.switch input { 
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: .4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: .4s;
}

input:checked + .slider {
  background-color: #28a745;
}

input:focus + .slider {
  box-shadow: 0 0 1px #28a745;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}
</style>